/**
 * "Så går det" on Home (pedagogy spec §6.3.4): three numbers from the last week, with a link to
 * the Progress page. Rendered only when the plan carries `evidence`. Slice C (P1-7, ruling 10):
 * flashcard reviews are a row of their own and never count in "Lösta utan hjälp"; `TodayTally` is
 * the "Idag: 4 kort, 1 uppgift" line on Home and after a finished pass.
 */
import { Link } from "react-router";
import { useLang, useT, useTp } from "@client/i18n";
import { formatNumber } from "@client/lib/format";
import type { TodayEvidence as TodayEvidenceDto } from "./types";

export function TodayEvidence({ evidence }: { evidence: TodayEvidenceDto }) {
  const t = useT();
  const { lang } = useLang();
  const n = (value: number) => formatNumber(value, lang);
  const rows: { label: string; value: string }[] = [
    {
      label: t("practice.today.evidence.topics"),
      value: t("practice.today.evidence.topicsValue", {
        started: n(evidence.topicsStarted),
        total: n(evidence.topicsTotal),
      }),
    },
    {
      label: t("practice.today.evidence.unaided"),
      value: n(evidence.solvedUnaided7d),
    },
    {
      label: t("practice.today.evidence.retests"),
      value: t("practice.today.evidence.retestsValue", {
        passed: n(evidence.retestsPassed7d),
        taken: n(evidence.retestsTaken7d),
      }),
    },
    {
      label: t("practice.today.evidence.cards"),
      value: n(evidence.cardReviews7d ?? 0),
    },
  ];

  return (
    <div className="mt-6 border-t border-ink-200 pt-4 dark:border-ink-800">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-medium text-ink-700 dark:text-ink-200">
          {t("practice.today.evidence.title")}
        </h3>
        <Link
          to="/progress"
          className="text-sm font-medium text-accent-800 underline underline-offset-2 hover:no-underline dark:text-accent-300"
        >
          {t("practice.today.evidence.open")}
        </Link>
      </div>
      <dl className="mt-2 grid gap-x-6 gap-y-2 sm:grid-cols-2">
        {rows.map((row) => (
          <div key={row.label}>
            <dt className="text-xs text-ink-600 dark:text-ink-400">{row.label}</dt>
            <dd className="text-lg tabular-nums text-ink-900 dark:text-ink-50">{row.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** "Idag: 4 kort, 1 uppgift" — what the student has done today; nothing before the first step. */
export function TodayTally({ evidence }: { evidence: TodayEvidenceDto }) {
  const t = useT();
  const tp = useTp();
  const { lang } = useLang();
  const cards = evidence.cardsToday ?? 0;
  const problems = evidence.practicedToday ?? 0;
  if (cards === 0 && problems === 0) return null;
  const n = (value: number) => formatNumber(value, lang);
  return (
    <p className="mt-3 text-sm text-ink-700 dark:text-ink-200" data-testid="today-tally">
      {t("practice.today.tally", {
        cards: tp("practice.today.tally.cards", cards, { count: n(cards) }),
        problems: tp("practice.today.tally.problems", problems, { count: n(problems) }),
      })}
    </p>
  );
}
